
import React, { useRef, useState } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight, Loader2, ShoppingBag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { HomeSection, Product, Ad } from '../types';
import { ProductCardLoader } from './ProductCardLoader';
import { OptimizedImage } from './OptimizedImage';

interface ProductSectionProps {
  title: string;
  subtitle?: string;
  hideTitle?: boolean;
  type: 'products' | 'shops' | 'categories';
  layout: 'grid' | 'carousel' | 'banner';
  productIds?: (string | number)[];
  productLimit?: number; // Add this
  categoryId?: number;
  adIds?: string[];
  viewAllLink?: string;
  hideViewAll?: boolean;
  allProducts: Product[];
  allAds?: Ad[]; // Add allAds
  isDark: boolean;
  priority?: boolean;
  onOpenCategory: (id: string) => void;
  onOpenDetails: (product: Product) => void;
  onAddToCart: (e: React.MouseEvent, product: Product) => void;
  addedToCartMap: { [key: string | number]: boolean };
}

export const ProductSection: React.FC<ProductSectionProps> = ({
  title,
  subtitle,
  hideTitle,
  type,
  layout,
  productIds,
  productLimit,
  categoryId,
  adIds,
  viewAllLink,
  hideViewAll,
  allProducts,
  allAds = [], // Default to empty array
  isDark,
  priority = false,
  onOpenCategory,
  onOpenDetails,
  onAddToCart,
  addedToCartMap
}) => {
  const navigate = useNavigate();

  const handleViewAll = () => {
    if (viewAllLink) {
      if (viewAllLink.startsWith('/')) navigate(viewAllLink);
      else if (viewAllLink.startsWith('http')) window.open(viewAllLink, '_blank');
      else navigate(viewAllLink);
    } else if (categoryId) {
      navigate(`/category/${categoryId}`);
    } else {
      navigate(`/search?q=${encodeURIComponent(title)}`);
    }
  };

  // Filter products based on section config
  let displayProducts: Product[] = [];
  if (type === 'categories') {
    // Categories don't display products directly, they render category cards
    displayProducts = allProducts; // pass through so the section doesn't return null
  } else if (categoryId) {
    displayProducts = allProducts.filter(p => p.categoryId?.toString() === categoryId.toString());
  } else {
    // Combine explicit productIds with tag-based auto-filtering (showInSections)
    const normalizedTitle = title.trim();
    displayProducts = allProducts.filter(p =>
      (productIds && productIds.some(id => id.toString() === p.id.toString())) ||
      (p.showInSections && p.showInSections.includes(normalizedTitle))
    );

    // Apply strict sorting based on productIds array if it exists
    if (productIds && productIds.length > 0) {
      displayProducts.sort((a, b) => {
        const indexA = productIds.findIndex(id => id.toString() === a.id.toString());
        const indexB = productIds.findIndex(id => id.toString() === b.id.toString());
        
        // If both are in the manual selection list, sort by their position in the list
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        // If only A is logically manually selected, it should come first
        if (indexA !== -1) return -1;
        // If only B is logically manually selected, it should come first
        if (indexB !== -1) return 1;
        // Otherwise keep original order
        return 0;
      });
    }

    // Fallback if no matches and it's a "products" type section: show all active products
    if (displayProducts.length === 0 && type === 'products') {
      displayProducts = allProducts.filter(p => p.isActive !== false);
    }
  }

  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);
  const isDragAction = useRef(false);

  // Apply Limit
  if (productLimit && productLimit > 0) {
    displayProducts = displayProducts.slice(0, productLimit);
  } else if (displayProducts.length > 12 && type === 'products' && (!productIds || productIds.length === 0)) {
     // Default fallback limit if no productIds and no explicit productLimit
    displayProducts = displayProducts.slice(0, 12);
  }

  if (displayProducts.length === 0) return null;

  // Limit items for carousel (no longer hardcoded to 10 if there's a limit/selection)
  const carouselProducts = layout === 'carousel' ? displayProducts : displayProducts;

  const scrollSection = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -320 : 320;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    startX.current = e.pageX - scrollRef.current.offsetLeft;
    scrollLeft.current = scrollRef.current.scrollLeft;
    isDragAction.current = false;
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setTimeout(() => {
      isDragAction.current = false;
    }, 50);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;

    if (e.buttons === 1) {
      e.preventDefault();
      const x = e.pageX - scrollRef.current.offsetLeft;
      const walk = (x - startX.current) * 1.5; // Scroll speed
      scrollRef.current.scrollLeft = scrollLeft.current - walk;

      if (Math.abs(e.pageX - startX.current - scrollRef.current.offsetLeft) > 5) {
        isDragAction.current = true;
      }
    }
  };

  const handleCardClick = (product: Product) => {
    // Only block if we've dragged more than a tiny bit (10px)
    if (isDragAction.current) return;
    onOpenDetails(product);
  };

  return (
    <div className="w-full flex flex-col items-center">
      {!hideTitle && (
        <div className="w-full flex flex-row items-center justify-between px-4 md:px-8 mb-2 max-w-[95%] gap-2" style={{ direction: 'rtl' }}>
          <div>
            <h2 className={`text-xl md:text-3xl font-black ${isDark ? 'text-white' : 'text-slate-900'}`}>{title}</h2>
            {subtitle && <p className={`text-xs md:text-base font-bold mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{subtitle}</p>}
          </div>
          {!hideViewAll && (
            <button
              onClick={handleViewAll}
              className={`text-[10px] md:text-sm font-black cursor-pointer transition-transform active:scale-95 px-3 md:px-4 py-1.5 md:py-2 rounded-full flex gap-1 md:gap-2 items-center shrink-0 ${isDark ? 'text-blue-400 bg-blue-500/10 hover:bg-blue-500/20' : 'text-blue-600 bg-blue-50 hover:bg-blue-100'}`}
            >
              عرض الكل
              <ChevronLeft size={16} />
            </button>
          )}
        </div>
      )}

      <div className="relative w-[95%] group/section min-h-[280px]">
        {layout === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6 py-4" style={{ direction: 'rtl' }}>
            {displayProducts.map((product, idx) => (
              <ProductCardLoader
                key={product.id}
                product={product}
                isDark={isDark}
                onOpenDetails={handleCardClick}
                onAddToCart={onAddToCart}
                isAdded={!!addedToCartMap[product.id]}
                priority={priority || idx < 4} // Subtle priority for first row in grid
              />
            ))}
          </div>
        ) : (
          <>
            <div
              ref={scrollRef}
              className={`flex gap-4 overflow-x-auto no-scrollbar scroll-container py-4 px-2 snap-x snap-proximity overscroll-x-contain cursor-grab select-none gpu-accel ${isDragging ? 'cursor-grabbing' : ''}`}
              style={{ direction: 'rtl', scrollBehavior: isDragging ? 'auto' : 'smooth' }}
              onMouseDown={handleMouseDown}
              onMouseLeave={handleMouseLeave}
              onMouseUp={handleMouseUp}
              onMouseMove={handleMouseMove}
            >
              {type === 'categories' ? (
                // Logic for category rendering
                allProducts.reduce((acc: any[], p) => {
                  if (p.category && !acc.find(c => c.name === p.category)) {
                    acc.push({ id: p.categoryId, name: p.category });
                  }
                  return acc;
                }, []).map((cat, idx) => (
                  <div
                    key={`${cat.id}-${idx}`}
                    onClick={() => onOpenCategory(cat.id.toString())}
                    className={`shrink-0 flex flex-col items-center gap-3 p-4 rounded-[2rem] border transition-all hover:scale-105 active:scale-95 cursor-pointer snap-center min-w-[120px] ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-slate-100 shadow-sm'}`}
                  >
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center p-3 ${isDark ? 'bg-blue-500/10' : 'bg-blue-50'}`}>
                      <ShoppingBag className="text-blue-500" size={32} />
                    </div>
                    <span className={`text-sm font-black whitespace-nowrap ${isDark ? 'text-white/80' : 'text-slate-700'}`}>{cat.name}</span>
                  </div>
                ))
              ) : (
                carouselProducts.map((product, idx) => {
                  // After every 4 products, try to render an Ad if we have adIds mapped
                  const adIndex = Math.floor(idx / 4);
                  const shouldRenderAd = (idx + 1) % 4 === 0 && adIds && adIds.length > adIndex;
                  const adToRender = shouldRenderAd ? allAds.find(a => String(a.id) === adIds[adIndex]) : null;

                  return (
                    <React.Fragment key={product.id}>
                      <ProductCardLoader
                        product={product}
                        isDark={isDark}
                        onOpenDetails={handleCardClick}
                        onAddToCart={onAddToCart}
                        isAdded={!!addedToCartMap[product.id]}
                        priority={priority || idx < 4}
                        isRow={true}
                      />
                      {adToRender && (
                        <div
                          className={`shrink-0 w-[240px] md:w-[320px] rounded-[2rem] md:rounded-[2.5rem] overflow-hidden relative cursor-pointer group shadow-sm hover:shadow-xl transition-all border snap-center ${isDark ? 'border-white/10' : 'border-black/5'} active:scale-[0.98]`}
                          onClick={() => adToRender.buttonLink && handleViewAll()}
                        >
                          {adToRender.image ? (
                            <OptimizedImage
                              src={adToRender.image}
                              alt={adToRender.subTitle || ''}
                              aspectRatio="1.5/1"
                              className="w-full h-full absolute inset-0 group-hover:scale-105 transition-transform duration-700 pointer-events-auto"
                            />
                          ) : (
                            <div className={`w-full h-full absolute inset-0 ${adToRender.gradient || 'bg-gradient-to-br from-blue-500 to-purple-600'}`} />
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent p-6 flex flex-col justify-end">
                            <h4 className="text-white font-black text-xl mb-1">{adToRender.subTitle}</h4>
                            <p className="text-white/80 font-bold text-sm">{adToRender.discount}</p>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </div>

            {/* Scroll Arrows */}
            <button
              onClick={() => scrollSection('left')}
              className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/3 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center z-20 opacity-0 group-hover/section:opacity-100 transition-all duration-300 shadow-xl border backdrop-blur-md active:scale-90 hidden md:flex ${isDark ? 'bg-black/50 border-white/20 text-white hover:bg-black/70' : 'bg-white/90 border-slate-200 text-slate-700 hover:bg-white'}`}
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={() => scrollSection('right')}
              className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/3 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center z-20 opacity-0 group-hover/section:opacity-100 transition-all duration-300 shadow-xl border backdrop-blur-md active:scale-90 hidden md:flex ${isDark ? 'bg-black/50 border-white/20 text-white hover:bg-black/70' : 'bg-white/90 border-slate-200 text-slate-700 hover:bg-white'}`}
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};
